import { useSettings } from "components/Clubs/hooks";
import { Application } from "components/ClubMemberManagement/types";
import { Form } from "components/Form/base/Form";
import { FormInput, FormSubmit } from "components/Form/next";
import Loader from "components/Loader";
import moment from "moment";
import { useApi } from "../../../../hooks/useApi";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useModal } from "../../../../hooks/useModal";
import GroupGuesser from "../../ClubMemberGroup/GroupGuesser";

export type ApplicationResponseAction = "accept" | "decline";

interface Props {
    action: ApplicationResponseAction;
    application: Application;
    clubSlug: string;
    callback?: () => void;
}

export const ApplicationResponseForm = ({ action, application, clubSlug, callback }: Props) => {
    const { t } = useTranslation("member-management");
    const api = useApi();
    const [isLoading, setIsLoading] = useState(false);
    const [setModal] = useModal();
    const [entryDateSource, { isLoading: isLoadingSettings }] = useSettings(
        "club_member_management.applications.entry_date_source",
        clubSlug
    );

    const defaultValues = useMemo(
        () => ({
            entry_date: moment(
                entryDateSource === "received" && application.created_at ? application.created_at : undefined
            ).format("YYYY-MM-DD")
        }),
        [entryDateSource, application.created_at]
    );

    const submitHandler = (data) => {
        setIsLoading(true);
        return api({
            url: `club-member/${clubSlug}/application/${application.id}/${action}`,
            method: "PUT",
            data,
            onBefore: () => setIsLoading(false),
            onSuccess: ({ data }) => {
                if (action !== "accept") {
                    callback?.();
                    return;
                }

                if (data?.group?.id) {
                    callback?.();
                    return;
                }

                setModal({
                    title: t("applications.responseForm.assignBillingGroupTitle"),
                    content: <GroupGuesser memberId={data.id} callback={callback} />,
                    show: true,
                    size: 50
                });
            }
        });
    };

    if (action === "accept" && isLoadingSettings) {
        return <Loader />;
    }

    return (
        <Form onSubmit={submitHandler} defaultValues={action === "accept" ? defaultValues : undefined}>
            {action === "accept" && (
                <FormInput.DatePicker
                    name="entry_date"
                    label={t("applications.responseForm.entryDateLabel")}
                    helperText={t("applications.responseForm.entryDateHelperText")}
                />
            )}
            <FormInput.Textarea name="reason" label={t("applications.responseForm.reasonLabel")} />
            <FormSubmit isLoading={isLoading} color={action === "accept" ? "primary" : "error"}>
                {action === "accept" ? t("applications.responseForm.acceptButton") : t("applications.responseForm.declineButton")}
            </FormSubmit>
        </Form>
    );
};
